import React from 'react';

const DeleteAccountPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Delete Account – EventIQ
          </h1>
          <p className="text-gray-600">
            Complete guide to permanently deleting your EventIQ account and data
          </p>
        </header>

        <main className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-10">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              How to Delete Your EventIQ Account
            </h2>
            <p className="text-gray-700 mb-6">
              We provide two straightforward methods to permanently delete your EventIQ account.
              Both methods result in complete and irreversible deletion of all your personal data
              from our systems.
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r">
              <p className="text-gray-800 font-medium">
                📱 <strong>Important:</strong> Account deletion is permanent and cannot be undone.
                All your event data, preferences, and profile information will be erased completely.
              </p>
            </div>
          </section>

          {/* Method 1: In-App Deletion */}
          <section>
            <div className="flex items-start mb-6">
              <div className="bg-green-100 text-green-800 rounded-full p-3 mr-4">
                <span className="text-xl font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Delete Account Within the EventIQ App (Recommended)
                </h3>
                <p className="text-gray-700">
                  The fastest and most direct way to delete your account and all associated data.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4 text-lg">
                Steps to delete in the app:
              </h4>
              <ol className="space-y-4 ml-4">
                <li className="flex items-start">
                  <span className="bg-white border border-gray-300 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</span>
                  <span className="text-gray-700">Open the EventIQ mobile app</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white border border-gray-300 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</span>
                  <span className="text-gray-700">Go to <strong>Settings</strong> → <strong>Account</strong> → <strong>Delete Account</strong></span>
                </li>
                {/* <li className="flex items-start">
                  <span className="bg-white border border-gray-300 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">3</span>
                  <span className="text-gray-700">Confirm your decision by entering your password</span>
                </li> */}
                <li className="flex items-start">
                  <span className="bg-white border border-gray-300 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">3</span>
                  <span className="text-gray-700">Your account and all data will be permanently deleted immediately</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Method 2: Email Request */}
          <section>
            <div className="flex items-start mb-6">
              <div className="bg-purple-100 text-purple-800 rounded-full p-3 mr-4">
                <span className="text-xl font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Request Account Deletion via Email
                </h3>
                <p className="text-gray-700">
                  If you cannot access the app, you can request account deletion by contacting our support team.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4 text-lg">
                Send an email to our support team:
              </h4>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600 mb-2">Email address:</p>
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <a 
                      href="mailto:divya@binalogy.in" 
                      className="text-blue-600 hover:text-blue-800 font-medium text-lg break-all"
                    >
                      divya@binalogy.in
                    </a>
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-2">Please include in your email:</p>
                  <ul className="list-disc pl-5 text-gray-700 space-y-1">
                    <li>Subject line: <span className="font-medium">"Account Deletion Request - EventIQ"</span></li>
                    <li>The email address associated with your EventIQ account</li>
                    <li>Your full name (as registered in the app)</li>
                    <li>Optional: Reason for account deletion (helps us improve)</li>
                  </ul>
                </div>
                <p className="text-gray-700 mt-4">
                  <strong>Processing time:</strong> We process email requests within 72 hours.
                  You will receive a confirmation email once your account and all data have been permanently deleted.
                </p>
              </div>
            </div>
          </section>

          {/* Data Deletion Policy */}
          <section className="border-t border-gray-200 pt-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Data Deletion Policy
            </h3>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-5 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                  What Gets Deleted
                </h4>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  <li>Your user profile information (name, email, phone number)</li>
                  <li>All events you created in EventIQ</li>
                  <li>Your event preferences and settings</li>
                  <li>Any uploaded media or files associated with your account</li>
                  <li>All authentication and session data</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-5 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                  Data Retention After Deletion
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="bg-green-100 text-green-800 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-gray-700">
                      <strong>Zero data retention:</strong> We do not retain any of your personal data after account deletion.
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-green-100 text-green-800 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-gray-700">
                      <strong>Complete Firebase Firestore deletion:</strong> All your data is permanently removed from our Firebase Firestore database.
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-green-100 text-green-800 rounded-full p-1 mr-3 mt-0.5">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-gray-700">
                      <strong>Immediate processing:</strong> Deletion occurs immediately when using the in-app option.
                    </p>
                  </div>
                </div>
              </div>

              {/* <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-r">
                <p className="text-gray-800">
                  <strong>Note about third-party services:</strong> If you used third-party services 
                  (like Google Sign-In) to access EventIQ, you may need to manage those permissions 
                  separately in your device or Google account settings.
                </p>
              </div> */}
            </div>
          </section>

          {/* Support Contact */}
          <section className="border-t border-gray-200 pt-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Need Help?
            </h3>
            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 rounded-lg p-3 mr-4">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">
                    Official Support Contact
                  </h4>
                  <p className="text-gray-700 mb-3">
                    For questions about account deletion or any other concerns:
                  </p>
                  <a 
                    href="mailto:divya@binalogy.in" 
                    className="inline-block bg-white border border-blue-300 text-blue-700 font-medium px-5 py-3 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Email: divya@binalogy.in
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        {/* <footer className="mt-10 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
          <p className="mb-2">EventIQ – Smart Event Management App</p>
          <p>© {new Date().getFullYear()} Binalogy. All rights reserved.</p>
          <p className="mt-4">
            This page is publicly accessible and does not require login.
          </p>
        </footer> */}
      </div>
    </div>
  );
};

export default DeleteAccountPage;